<template>
  <div :class="wrapperClasses">
    <div class="custom-file">
      <input
        ref="input"
        type="file"
        class="custom-file-input"
        :accept="accept"
        :disabled="disabled"
        :multiple="multiple"
        :class="inputStateClasses"
        v-bind="passthroughAttrs"
        @change="handleChange"
      />
      <label class="custom-file-label" :for="$attrs.id">
        <slot name="file-name" :names="fileNames">
          {{ displayLabel }}
        </slot>
      </label>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'HdFormFile',

  inheritAttrs: false,

  props: {
    value: {
      default: null
    },
    modelValue: {
      default: undefined
    },
    placeholder: {
      type: String,
      default: 'Choose a file…'
    },
    accept: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: null,
      validator: (value) => value == null || ['sm', 'lg'].includes(value)
    },
    state: {
      type: [Boolean, String],
      default: null
    }
  },

  emits: ['input', 'update:modelValue', 'change'],

  data() {
    return {
      localValue: null
    };
  },

  created() {
    this.syncLocalValue(this.currentValue);
  },

  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },

    wrapperClasses() {
      const classes = [this.$attrs.class].flat().filter(Boolean);

      if (this.size === 'lg') classes.push('custom-file-lg');

      return classes;
    },

    inputStateClasses() {
      const classes = [];

      if (this.state === true) classes.push('is-valid');
      if (this.state === false) classes.push('is-invalid');

      return classes;
    },

    fileNames() {
      const value = this.localValue;

      if (!value) return [];
      if (Array.isArray(value)) return value.map((file) => file.name);

      return [value.name];
    },

    displayLabel() {
      if (this.fileNames.length === 0) return this.placeholder;

      return this.fileNames.join(', ');
    },

    passthroughAttrs() {
      const { class: _class, ...attrs } = this.$attrs;

      return attrs;
    }
  },

  watch: {
    currentValue(value) {
      this.syncLocalValue(value);
    }
  },

  methods: {
    syncLocalValue(value) {
      this.localValue = value;

      if (value == null || (Array.isArray(value) && value.length === 0)) {
        this.resetInput();
      }
    },

    resetInput() {
      if (this.$refs.input) this.$refs.input.value = '';
    },

    handleChange(event) {
      const fileList = event.target.files;
      let value;

      if (this.multiple) {
        value = fileList.length ? Array.from(fileList) : [];
      } else {
        value = fileList.length ? fileList[0] : null;
      }

      this.localValue = value;
      this.$emit('input', value);
      this.$emit('update:modelValue', value);
      this.$emit('change', event);
    }
  }
});
</script>
